import axios from "axios";
import { store } from "../app/store";
import { setAuthenticated, setVendor } from "../redux/slices/authSlice";

// Resolve API base URL adapting to Android Emulator (10.0.2.2) when needed
const resolveBaseURL = () => {
  const envBase = process.env.REACT_APP_API_URL;
  if (!envBase) {
    if (process.env.NODE_ENV !== "production") {
      return "http://localhost:3001";
    }
    throw new Error("REACT_APP_API_URL is required in production builds.");
  }

  try {
    const url = new URL(envBase);
    // If env points to localhost but the app is served from a different host (e.g., 10.0.2.2 in Android WebView),
    // align the API hostname to the current page's hostname to keep both on the same machine.
    if (typeof window !== "undefined" && window.location) {
      const currentHost = window.location.hostname; // e.g., 10.0.2.2 when accessed via Android emulator
      if (
        url.hostname === "localhost" &&
        currentHost &&
        currentHost !== "localhost"
      ) {
        url.hostname = currentHost;
        return url.toString();
      }
    }

    return envBase;
  } catch (_e) {
    // Fallback to env if URL parsing fails
    return envBase;
  }
};

const api = axios.create({
  baseURL: resolveBaseURL(),
  headers: { "Content-Type": "application/json" },
});

// Add a request interceptor to add the JWT token to headers, if available.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("jwtToken");
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

// Global response interceptor to handle expired/invalid tokens or missing vendor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = String(error?.config?.url || "");
    const message = String(error?.response?.data?.error || "");
    const isAuthFlowRequest = /\/vendor\/(auth\/(login|register|check-phone|check-email-status|forgot-password|reset-password|complete-registration)|otp\/)/.test(url);

    // 1) Always logout on 401 (invalid/expired token)
    if (status === 401 && !isAuthFlowRequest) {
      localStorage.removeItem("jwtToken");
      localStorage.removeItem("vendor");
      store.dispatch(setAuthenticated(false));
      store.dispatch(setVendor(null));
      window.location.href = "/auth/sign-in";
      return Promise.reject(error);
    }

    // 2) Do NOT logout on 403 for business rules (e.g., not verified/onboarding)
    const isBusiness403 =
      status === 403 && (
        message.toLowerCase().includes("not verified") ||
        message.toLowerCase().includes("onboarding")
      );
    if (isBusiness403) {
      return Promise.reject(error);
    }

    // 3) Logout on 403 only for core identity endpoints indicating wrong role
    const isCoreIdentity403 =
      status === 403 && (url.includes("/vendor/auth/me") || url.includes("/vendor/store/me"));
    if (isCoreIdentity403) {
      localStorage.removeItem("jwtToken");
      localStorage.removeItem("vendor");
      store.dispatch(setAuthenticated(false));
      store.dispatch(setVendor(null));
      window.location.href = "/auth/sign-in";
      return Promise.reject(error);
    }

    // 4) Missing vendor profile treated as logged-out (404 on /vendor/store/me)
    if (status === 404 && url.includes("/vendor/store/me")) {
      localStorage.removeItem("jwtToken");
      localStorage.removeItem("vendor");
      store.dispatch(setAuthenticated(false));
      store.dispatch(setVendor(null));
      window.location.href = "/auth/sign-in";
      return Promise.reject(error);
    }

    if (status === 429) {
      if (typeof window !== "undefined" && window.location?.pathname !== "/error/429") {
        window.location.href = "/error/429";
      }
    }

    return Promise.reject(error);
  }
);

export default api;
